import axios from 'axios'

const client = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

const ACCESS_KEY = 'token'
const REFRESH_KEY = 'refresh_token'

function isPersistent() {
  return !!localStorage.getItem(ACCESS_KEY)
}

function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY) || sessionStorage.getItem(ACCESS_KEY)
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY) || sessionStorage.getItem(REFRESH_KEY)
}

function storeTokens(accessToken: string, refreshToken: string) {
  if (isPersistent()) {
    localStorage.setItem(ACCESS_KEY, accessToken)
    localStorage.setItem(REFRESH_KEY, refreshToken)
  } else {
    sessionStorage.setItem(ACCESS_KEY, accessToken)
    sessionStorage.setItem(REFRESH_KEY, refreshToken)
  }
}

function clearTokens() {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  sessionStorage.removeItem(ACCESS_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
}

// Request interceptor
client.interceptors.request.use(config => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Single in-flight refresh to avoid duplicate refresh requests on parallel 401s.
let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null
  try {
    const { data } = await axios.post(
      '/api/v1/auth/refresh',
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )
    const payload = data?.data
    const newAccess = payload?.access_token
    const newRefresh = payload?.refresh_token || refreshToken
    if (newAccess) {
      storeTokens(newAccess, newRefresh)
      return newAccess
    }
    return null
  } catch {
    return null
  }
}

// Response interceptor
client.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config
    const url: string = original?.url || ''
    const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/refresh')

    if (error.response?.status === 401 && original && !original._retried && !isAuthRoute) {
      original._retried = true
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null
        })
      }
      const newToken = await refreshPromise
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`
        return client(original)
      }
    }

    if (error.response?.status === 401 && !url.includes('/auth/login')) {
      clearTokens()
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export default client
